import React, { useState } from 'react';
import DatePicker from "react-datepicker";
import {ko} from 'date-fns/locale/ko'
import 'react-datepicker/dist/react-datepicker.css';
import useUIValidator from "common/validation/component/UseUIValidator";
import {ValidationRuleNames} from "common/validation/rules/ValidationRule";
import {ValidationRuleBytesProps} from "common/validation/rules/ValidationRuleBytes";
import styled from "styled-components";
import classNames from "classnames";
import StringUtil from "utils/StringUtil";

export interface DatePickerBaseProps {
	dateFormat?: string;
	displayFormat?: string;
	displayOnly?: boolean;
	showMonthYearPicker?: boolean;
	showFullMonthYearPicker?: boolean;
	showFourColumnMonthYearPicker?: boolean;
	showYearPicker?: boolean;
}

export interface DatePickerProps extends DatePickerBaseProps {
	value?: Date;
	valueChanged?: (value: Date) => void;
	minDate?: Date;
	maxDate?: Date;
	withoutWrapper?: boolean;
	showToday?: boolean;
	className?: string;
	rules?: Array<ValidationRuleNames>;
	bytes?: ValidationRuleBytesProps;
}

const StyledDiv = styled.div`
	margin: 10px;
	display: flex;
	justify-content: center;
`

// DatePicker
function UIDatePicker({
						  bytes,
						  className = "tf tf-md w100p",
						  dateFormat = "yyyy-MM-dd(eee)",
						  maxDate,
						  minDate,
						  rules,
						  showToday = true,
						  value,
						  valueChanged
					  }: DatePickerProps) {
	const [format, setFormat] = React.useState(dateFormat);
	const {required, valueChecker, errorClassName} = useUIValidator({rules: rules, bytes: bytes})
	const datePicker = React.useRef<any>();

	React.useEffect(() => {
		valueChecker.setValue(value);
	}, [value]);

	React.useEffect(() => {
		if(datePicker.current) {
			datePicker.current.input.setAttribute('data-security', 'off');
			datePicker.current.input.setAttribute('data-value-rule', rules);
			datePicker.current.input.setAttribute('data-value-bytes', bytes);
		}
	}, [datePicker]);

	const years = Array.from({length: ((new Date().getFullYear()+50) - 1920 + 1)}, (v, k) => k + 1920);
	const months = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",];

	const picker: React.ReactElement = (
		<div className="datepicker">
			<DatePicker
				ref={datePicker}
				className={classNames("tf tf-md w100p", errorClassName)}
				dateFormat={format}
				locale={ko}
				minDate={minDate}
				maxDate={maxDate}
				selected={value}
				onChange={(date) => {
					if (valueChanged != null) {
						valueChanged(date!);
					}
				}}
				todayButton={showToday ? "오늘" : undefined}
				required={required}
				onFocus={() => {
					if (StringUtil.startsWith(dateFormat, "yyyy-MM-dd")) {
						setFormat("yyyy-MM-dd");
					}
				}}
				onBlur={() => {
					if (StringUtil.startsWith(dateFormat, "yyyy-MM-dd")) {
						setFormat(dateFormat);
					}
				}}
				renderCustomHeader={({
										 date,
										 changeYear,
										 changeMonth,
										 decreaseMonth,
										 increaseMonth,
										 prevMonthButtonDisabled,
										 nextMonthButtonDisabled,
									 }) => (
					<StyledDiv>
						<button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>{"<"}</button>
						<select
							className={"tf tf-sm w90"}
							value={date?.getFullYear()}
							onChange={({target: {value}}) => changeYear(Number(value))}
						>
							{years.map((option) => (
								<option key={option} value={option}>
									{option}년
								</option>
							))}
						</select>

						<select
							className={"tf tf-sm w60"}
							value={months[date.getMonth()]}
							onChange={({target: {value}}) =>
								changeMonth(months.indexOf(value))
							}
						>
							{months.map((option) => (
								<option key={option} value={option}>
									{option}월
								</option>
							))}
						</select>

						<button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>{">"}</button>
						{showToday && <div style={{width:"48px"}}></div>}
					</StyledDiv>
				)}
			/>
		</div>
	);
	return picker;
}

export default UIDatePicker;
